import { API_URL, authenticatedFetch } from "../config/api";

// Define interfaces for document operations
interface DocumentUploadResponse {
  id: string;
  name: string;
  size: number;
  mimetype: string;
  uploadedBy: string;
  uploadedAt: string;
  projectId?: string;
}

interface DocumentListResponse {
  results: DocumentInterface[];
  total: number;
  page: number;
  size: number;
}

interface DocumentInterface {
  id: string;
  name: string;
  size: number;
  mimetype: string;
  uploadedBy: string;
  uploadedAt: string;
  projectId?: string;
  url?: string;
}

export interface ProjectDocumentOwner {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_email_verified: boolean;
  created_at: string;
  updated_at: string | null;
  active_to: string;
}

export interface ProjectDocumentInterface {
  id: string;
  project_id: string;
  name: string;
  file_name: string;
  file_size: number;
  content_type: string;
  description: string;
  document_kind: 'Task' | 'Project' | 'Meeting' | 'ActionItem' | 'DailyUpdate';
  version: number;
  url: string;
  document: string;
  created_at: string;
  updated_at: string;
  owner: ProjectDocumentOwner;
  source_id?: string; // ID of the source record (daily update, action item, etc.)
}

interface ProjectDocumentsResponse {
  total: number;
  page: number;
  size: number;
  results: ProjectDocumentInterface[];
}

/**
 * Upload a document file
 * @param {string} token - The authorization token
 * @param {File} file - The file to upload
 * @param {string} projectId - Optional project ID to associate with the document
 * @returns {Promise<DocumentUploadResponse>} The uploaded document info
 */
export const uploadDocument = async (
  _token: string, 
  file: File, 
  projectId?: string
): Promise<DocumentUploadResponse> => {
  try {
    console.log('Uploading document to project:', projectId, 'File:', file.name);
    
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) {
      formData.append('projectId', projectId);
    }

    const r = await authenticatedFetch(`${API_URL}/documents/`, {
      method: 'POST',
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to upload document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentUploadResponse = await r.json();
    console.log('Document upload result:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in uploadDocument:', e);
    throw new Error(e.message || 'Failed to upload document');
  }
};

/**
 * Upload multiple documents
 * @param {string} token - The authorization token
 * @param {File[]} files - Array of files to upload
 * @param {string} projectId - Optional project ID to associate with the documents
 * @returns {Promise<DocumentUploadResponse[]>} Array of uploaded document info
 */
export const uploadMultipleDocuments = async (
  _token: string,
  files: File[],
  projectId?: string
): Promise<DocumentUploadResponse[]> => {
  try {
    console.log('Uploading multiple documents:', files.map(f => f.name), 'to project:', projectId);
    
    // Try the bulk upload endpoint first
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      if (projectId) {
        formData.append('projectId', projectId);
      }

      const r = await authenticatedFetch(`${API_URL}/documents/upload/multiple`, {
        method: 'POST',
        body: formData
      });

      if (r.ok) {
        const data: DocumentUploadResponse[] = await r.json();
        console.log('Bulk upload successful:', data);
        return data;
      } else {
        console.log('Bulk upload endpoint failed, falling back to individual uploads');
      }
    } catch (bulkError) {
      console.log('Bulk upload failed, falling back to individual uploads:', bulkError);
    }

    // Fallback: upload files individually
    const results: DocumentUploadResponse[] = [];
    const errors: string[] = [];

    for (const file of files) {
      try {
        const result = await uploadDocument('', file, projectId);
        results.push(result);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        errors.push(`${file.name}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    if (errors.length > 0 && results.length === 0) {
      throw new Error(`All uploads failed: ${errors.join(', ')}`);
    }

    if (errors.length > 0) {
      console.warn(`Some uploads failed: ${errors.join(', ')}`);
    }

    console.log(`Successfully uploaded ${results.length}/${files.length} files`);
    return results;
  } catch (e: Error | any) {
    console.error('Error in uploadMultipleDocuments:', e);
    throw new Error(e.message || 'Failed to upload documents');
  }
};

/**
 * Get list of documents
 * @param {string} token - The authorization token
 * @param {string} projectId - Optional project ID to filter documents
 * @param {number} page - Page number for pagination
 * @param {number} size - Number of items per page
 * @returns {Promise<DocumentListResponse>} List of documents
 */
export const getDocuments = async (
  _token: string,
  projectId?: string,
  page: number = 1,
  size: number = 20
): Promise<DocumentListResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    if (projectId) {
      params.append('projectId', projectId);
    }

    const r = await authenticatedFetch(`${API_URL}/documents/?${params.toString()}`);

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentListResponse = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Get documents for a specific project
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @returns {Promise<DocumentListResponse>} List of project documents
 */
export const getProjectDocuments = async (
  _token: string,
  projectId: string,
  options?: { archived?: boolean; page?: number; size?: number }
): Promise<DocumentListResponse> => {
  try {
    console.log('Fetching documents for project:', projectId);

    const url = new URL(`${API_URL}/projects/${projectId}/documents`);
    const size = options?.size ?? 1000;
    const page = options?.page ?? 1;
    url.searchParams.set('size', String(size));
    url.searchParams.set('page', String(page));
    if (options?.archived) {
      url.searchParams.set('archived', 'true');
    }

    const r = await authenticatedFetch(url.toString(), {
      headers: {
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      console.error('Failed to fetch project documents:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentListResponse = await r.json();
    console.log('Project documents API response:', data);

    // Additional client-side filtering as backup
    if (data.results) {
      const filteredResults = data.results.filter(doc =>
        doc.projectId === projectId || !doc.projectId
      );
      console.log('Filtered results for project', projectId, ':', filteredResults);
      return {
        ...data,
        results: filteredResults,
        total: filteredResults.length
      };
    }

    return data;
  } catch (e: Error | any) {
    console.error('Error in getProjectDocuments:', e);
    throw new Error(e.message || 'Failed to fetch project documents');
  }
};

/**
 * Get project documents using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {number} page - Page number for pagination
 * @param {number} size - Number of items per page
 * @param {string} document_kind - Optional filter by document type
 * @returns {Promise<ProjectDocumentsResponse>} List of project documents
 */
export const getProjectDocumentsV1 = async (
  _token: string,
  projectId: string,
  page: number = 1,
  size: number = 1000,
  document_kind?: string
): Promise<ProjectDocumentsResponse> => {
  try {
    console.log('Fetching project documents v1 for project:', projectId);

    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    if (document_kind) {
      params.append('document_kind', document_kind);
    }

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/documents?${params.toString()}`);

    if (!r.ok) {
      console.error('Failed to fetch project documents v1:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    console.log('Project documents v1 API response:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in getProjectDocumentsV1:', e);
    throw new Error(e.message || 'Failed to fetch project documents');
  }
};

/**
 * Upload a document to a project using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {File} file - The file to upload
 * @param {string} description - Optional description for the document
 * @returns {Promise<string>} The uploaded document ID
 */
export const uploadProjectDocumentV1 = async (
  _token: string,
  projectId: string,
  file: File,
  description?: string
): Promise<string> => {
  try {
    console.log('Uploading document to project v1:', projectId, 'File:', file.name);
    
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/document`, {
      method: 'POST',
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to upload project document v1:', r.status, r.statusText);
      
      // Special handling for authentication errors
      if (r.status === 401) {
        const authError = new Error('Authentication failed. Your session may have expired. Please refresh the page and try again.');
        (authError as any).isAuthError = true;
        (authError as any).statusCode = 401;
        throw authError;
      }
      
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    console.log('Project document upload v1 result:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in uploadProjectDocumentV1:', e);
    // Re-throw auth errors as-is to preserve the flag
    if (e.isAuthError) {
      throw e;
    }
    throw new Error(e.message || 'Failed to upload project document');
  }
};

/**
 * Update a project document using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {File} file - The file to upload
 * @param {string} description - Optional description for the document
 * @returns {Promise<void>} Success response
 */
export const updateProjectDocumentV1 = async (
  _token: string,
  projectId: string,
  file: File,
  description?: string
): Promise<void> => {
  try {
    console.log('Updating project document v1:', projectId, 'File:', file.name);
    
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/document`, {
      method: 'PUT',
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to update project document v1:', r.status, r.statusText);
      
      // Special handling for authentication errors
      if (r.status === 401) {
        const authError = new Error('Authentication failed. Your session may have expired. Please refresh the page and try again.');
        (authError as any).isAuthError = true;
        (authError as any).statusCode = 401;
        throw authError;
      }
      
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    console.log('Project document update v1 successful');
  } catch (e: Error | any) {
    console.error('Error in updateProjectDocumentV1:', e);
    // Re-throw auth errors as-is to preserve the flag
    if (e.isAuthError) {
      throw e;
    }
    throw new Error(e.message || 'Failed to update project document');
  }
};

/**
 * Delete a project document using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} documentId - Document ID to delete
 * @returns {Promise<string>} Success response
 */
export const deleteProjectDocumentV1 = async (
  _token: string,
  projectId: string,
  documentId: string
): Promise<string> => {
  try {
    const url = `${API_URL}/projects/${projectId}/document/${documentId}`;
    console.log('Deleting project document v1:', projectId, 'documentId:', documentId);

    const r = await authenticatedFetch(url, {
      method: 'DELETE',
    });

    if (!r.ok) {
      const errorText = await r.text();
      console.error('Failed to delete project document v1:', r.status, r.statusText);

      if (r.status === 401) {
        const authError = new Error('Authentication failed. Your session may have expired. Please refresh the page and try again.');
        (authError as any).isAuthError = true;
        (authError as any).statusCode = 401;
        throw authError;
      }

      throw new Error(`HTTP ${r.status}: ${r.statusText}. ${errorText}`);
    }

    const data = await r.json();
    return data;
  } catch (e: Error | any) {
    console.error('Error in deleteProjectDocumentV1:', e);
    if (e.isAuthError) {
      throw e;
    }
    throw new Error(e.message || 'Failed to delete project document');
  }
};

/**
 * Get a specific project document using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} fileName - File name
 * @returns {Promise<string>} The document data
 */
export const getProjectDocumentV1 = async (
  _token: string,
  projectId: string,
  fileName: string
): Promise<string> => {
  try {
    console.log('Fetching project document v1:', projectId, 'fileName:', fileName);

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/document/${encodeURIComponent(fileName)}`);

    if (!r.ok) {
      console.error('Failed to fetch project document v1:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    console.log('Project document v1 response:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in getProjectDocumentV1:', e);
    throw new Error(e.message || 'Failed to fetch project document');
  }
};

/**
 * Get project item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Item ID
 * @returns {Promise<DocumentInterface>} Project item document
 */
export const getProjectItemDocument = async (
  _token: string,
  projectId: string,
  itemId: string
): Promise<DocumentInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/items/${itemId}/document`, {
      headers: {
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create project item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Item ID
 * @param {File} file - The file to upload
 * @returns {Promise<DocumentInterface>} Created document
 */
export const createProjectItemDocument = async (
  _token: string,
  projectId: string,
  itemId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/items/${itemId}/document`, {
      method: 'POST',
      body: formData
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Update project item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Item ID
 * @param {File} file - The new file to upload
 * @returns {Promise<DocumentInterface>} Updated document
 */
export const updateProjectItemDocument = async (
  _token: string,
  projectId: string,
  itemId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/items/${itemId}/document`, {
      method: 'PUT',
      body: formData
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Delete project item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Item ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteProjectItemDocument = async (
  _token: string,
  projectId: string,
  itemId: string
): Promise<boolean> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/items/${itemId}/document`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
      }
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    return true;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Download a document
 * @param {string} token - The authorization token
 * @param {string} documentId - Document ID
 * @returns {Promise<Blob>} Document file blob
 */
export const downloadDocument = async (_token: string, url: string): Promise<{blob: Blob, headers: Headers} | undefined> => {
  try {
    console.log('Attempting to download document:', url);

    // First try the download endpoint
    const downloadResponse = await authenticatedFetch(url);

    console.log('Download endpoint response:', downloadResponse.status, downloadResponse.statusText);

    if (downloadResponse.ok) {
      const blob = await downloadResponse.blob();
      const headers = downloadResponse.headers;
      console.log('Successfully downloaded via download endpoint:', blob.size, 'bytes');
      return {blob, headers};
    }

    // If download endpoint failed, try to get metadata first
    console.log('Download endpoint failed, trying metadata approach');
    
    // try {
    //   const metadataResponse = await fetch(`${API_URL}/documents/${documentId}`, {
    //     headers: {
    //       'content-type': 'application/json',
    //       'authorization': 'Bearer ' + token
    //     }
    //   });

    //   console.log('Metadata endpoint response:', metadataResponse.status, metadataResponse.statusText);

    //   if (!metadataResponse.ok) {
    //     throw new Error(`HTTP ${metadataResponse.status}: ${metadataResponse.statusText}`);
    //   }

    //   // Check if the response is actually JSON
    //   const contentType = metadataResponse.headers.get('content-type');
    //   console.log('Metadata response content-type:', contentType);
      
    //   if (!contentType || !contentType.includes('application/json')) {
    //     console.log('Metadata endpoint returned non-JSON content, treating as file');
    //     // If it's not JSON, treat the response as the file itself
    //     const blob = await metadataResponse.blob();
    //     console.log('Downloaded file directly from metadata endpoint:', blob.size, 'bytes');
    //     return blob;
    //   }

    //   // Parse as JSON metadata
    //   const responseText = await metadataResponse.text();
    //   console.log('Metadata response text (first 200 chars):', responseText.substring(0, 200));
      
    //   let metadata: DocumentInterface;
    //   try {
    //     metadata = JSON.parse(responseText);
    //   } catch (parseError) {
    //     console.error('Failed to parse metadata JSON:', parseError);
    //     console.log('Response was not valid JSON, treating as file content');
    //     // Convert the text response to a blob
    //     const blob = new Blob([responseText], { type: 'application/octet-stream' });
    //     return blob;
    //   }
      
    //   if (metadata.url) {
    //     // If there's a direct URL, download from there
    //     console.log('Using direct URL from metadata:', metadata.url);
        
    //     // Fix incomplete URLs that start with "https://api/" or "https://api/v1/"
    //     let downloadUrl = metadata.url;
    //     if (downloadUrl.startsWith('https://api/v1/')) {
    //       // Replace "https://api/v1/" with the full API_URL
    //       downloadUrl = downloadUrl.replace('https://api/v1/', `${API_URL}/`);
    //       console.log('Fixed incomplete URL (v1) to:', downloadUrl);
    //     } else if (downloadUrl.startsWith('https://api/')) {
    //       // Replace "https://api/" with the full API_URL base
    //       const apiBase = API_URL.replace('/api/v1', ''); // Remove /api/v1 to get base domain
    //       downloadUrl = downloadUrl.replace('https://api/', `${apiBase}/api/`);
    //       console.log('Fixed incomplete URL to:', downloadUrl);
    //     }
        
    //     const fileResponse = await fetch(downloadUrl);
    //     if (!fileResponse.ok) {
    //       throw new Error(`Failed to download from URL: ${fileResponse.status} ${fileResponse.statusText}`);
    //     }
    //     const blob = await fileResponse.blob();
    //     console.log('Downloaded from direct URL:', blob.size, 'bytes');
    //     return blob;
    //   }

    //   throw new Error('No download URL available in document metadata');

    // } catch (metadataError) {
    //   console.error('Metadata approach failed:', metadataError);
    //   throw new Error(`Failed to download document: ${metadataError instanceof Error ? metadataError.message : String(metadataError)}`);
    // }

  } catch (e: Error | any) {
    console.error('Download error:', e);
    throw new Error(e.message || 'Failed to download document');
  }
};

/**
 * Get document metadata
 * @param {string} token - The authorization token
 * @param {string} documentId - Document ID
 * @returns {Promise<DocumentInterface>} Document metadata
 */
export const getDocumentById = async (_token: string, documentId: string): Promise<DocumentInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/documents/${documentId}`, {
      headers: {
        'content-type': 'application/json',
      }
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Delete a document
 * @param {string} token - The authorization token
 * @param {string} documentId - Document ID to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteDocument = async (_token: string, documentId: string): Promise<boolean> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/documents/${documentId}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
      }
    });

    if (!r.ok) {
      console.error('Failed to delete document:', r.status, r.statusText);

      if (r.status === 401) {
        const authError = new Error('Authentication failed. Your session may have expired. Please refresh the page and try again.');
        (authError as any).isAuthError = true;
        (authError as any).statusCode = 401;
        throw authError;
      }

      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    return true;
  } catch (e: Error | any) {
    console.error('Error in deleteDocument:', e);
    if (e.isAuthError) {
      throw e;
    }
    throw new Error(e.message || e);
  }
};

/**
 * Update document metadata
 * @param {string} token - The authorization token
 * @param {string} documentId - Document ID to update
 * @param {Partial<DocumentInterface>} data - Updated document data
 * @returns {Promise<DocumentInterface>} Updated document
 */
export const updateDocument = async (
  _token: string,
  documentId: string,
  data: Partial<DocumentInterface>
): Promise<DocumentInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/documents/${documentId}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const result: DocumentInterface = await r.json();
    return result;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Get a daily update document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Daily update record ID
 * @param {string} fileName - File name
 * @returns {Promise<DocumentInterface>} Document info
 */
export const getDailyUpdateDocument = async (
  _token: string,
  projectId: string,
  recordId: string,
  fileName: string
): Promise<DocumentInterface> => {
  try {
    console.log(`Getting daily update document for project ${projectId}, record ${recordId}, file ${fileName}`);

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/daily-update/${recordId}/document/${encodeURIComponent(fileName)}`, {
      method: 'GET',
      headers: {
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      console.error('Failed to get daily update document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Daily update document loaded:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in getDailyUpdateDocument:', e);
    throw new Error(e.message || 'Failed to get daily update document');
  }
};

/**
 * Create a daily update document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Daily update record ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Created document info
 */
export const createDailyUpdateDocument = async (
  _token: string,
  projectId: string,
  recordId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Creating daily update document for project ${projectId}, record ${recordId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/daily-update/${recordId}/document`, {
      method: 'POST',
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to create daily update document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Daily update document created:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in createDailyUpdateDocument:', e);
    throw new Error(e.message || 'Failed to create daily update document');
  }
};

/**
 * Update a daily update document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Daily update record ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Updated document info
 */
export const updateDailyUpdateDocument = async (
  _token: string,
  projectId: string,
  recordId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Updating daily update document for project ${projectId}, record ${recordId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/daily-update/${recordId}/document`, {
      method: 'PUT',
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to update daily update document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Daily update document updated:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in updateDailyUpdateDocument:', e);
    throw new Error(e.message || 'Failed to update daily update document');
  }
};

/**
 * Delete a daily update document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Daily update record ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteDailyUpdateDocument = async (
  _token: string,
  projectId: string,
  recordId: string
): Promise<boolean> => {
  try {
    console.log(`Deleting daily update document for project ${projectId}, record ${recordId}`);

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/daily-update/${recordId}/document`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      console.error('Failed to delete daily update document:', r.status, r.statusText);

      if (r.status === 401) {
        const authError = new Error('Authentication failed. Your session may have expired. Please refresh the page and try again.');
        (authError as any).isAuthError = true;
        (authError as any).statusCode = 401;
        throw authError;
      }

      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    console.log('Daily update document deleted successfully');
    return true;
  } catch (e: Error | any) {
    console.error('Error in deleteDailyUpdateDocument:', e);
    if (e.isAuthError) {
      throw e;
    }
    throw new Error(e.message || 'Failed to delete daily update document');
  }
};

// Meeting Note Document Functions

/**
 * Build the download URL for a meeting note document.
 * The filename segment is required — the bare `/document` path only accepts
 * POST and DELETE, so omitting it yields a 405 (FD2-1220).
 * @param {string} projectId - Project ID
 * @param {string} recordId - Meeting note record ID
 * @param {string} fileName - File name
 * @returns {string} Absolute URL to the stored document
 */
export const buildMeetingNoteDocumentUrl = (
  projectId: string,
  recordId: string,
  fileName: string
): string =>
  `${API_URL}/projects/${projectId}/meeting-notes/${recordId}/document/${encodeURIComponent(fileName)}`;

/**
 * Download a meeting note document
 * @param {string} projectId - Project ID
 * @param {string} recordId - Meeting note record ID
 * @param {string} fileName - File name
 * @returns {Promise<{blob: Blob, headers: Headers}>} File contents and response headers
 */
export const downloadMeetingNoteDocument = async (
  projectId: string,
  recordId: string,
  fileName: string
): Promise<{blob: Blob, headers: Headers}> => {
  const url = buildMeetingNoteDocumentUrl(projectId, recordId, fileName);

  try {
    console.log(`Downloading meeting note document for project ${projectId}, record ${recordId}, file ${fileName}`);

    const r = await authenticatedFetch(url, { method: 'GET' });

    if (!r.ok) {
      console.error('Failed to download meeting note document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    return { blob: await r.blob(), headers: r.headers };
  } catch (e: Error | any) {
    console.error('Error in downloadMeetingNoteDocument:', e);
    throw new Error(e.message || 'Failed to download meeting note document');
  }
};

/**
 * Create a meeting note document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Meeting note record ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Created document info
 */
export const createMeetingNoteDocument = async (
  _token: string,
  projectId: string,
  recordId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Creating meeting note document for project ${projectId}, record ${recordId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/meeting-notes/${recordId}/document`, {
      method: 'POST',
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to create meeting note document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Meeting note document created:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in createMeetingNoteDocument:', e);
    throw new Error(e.message || 'Failed to create meeting note document');
  }
};

/**
 * Delete a meeting note document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Meeting note record ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteMeetingNoteDocument = async (
  _token: string,
  projectId: string,
  recordId: string
): Promise<boolean> => {
  try {
    const url = `${API_URL}/projects/${projectId}/meeting-notes/${recordId}/document`;
    console.log(`Deleting meeting note document for project ${projectId}, record ${recordId}`);

    const r = await authenticatedFetch(url, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      console.error('Failed to delete meeting note document:', r.status, r.statusText);

      if (r.status === 401) {
        const authError = new Error('Authentication failed. Your session may have expired. Please refresh the page and try again.');
        (authError as any).isAuthError = true;
        (authError as any).statusCode = 401;
        throw authError;
      }

      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    console.log('Meeting note document deleted successfully');
    return true;
  } catch (e: Error | any) {
    console.error('Error in deleteMeetingNoteDocument:', e);
    if (e.isAuthError) {
      throw e;
    }
    throw new Error(e.message || 'Failed to delete meeting note document');
  }
};

// Action Items Document Functions

/**
 * Get an action item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Action item ID
 * @param {string} fileName - File name
 * @returns {Promise<DocumentInterface>} Document info
 */
export const getActionItemDocument = async (
  _token: string,
  projectId: string,
  itemId: string,
  fileName: string
): Promise<DocumentInterface> => {
  try {
    console.log(`Getting action item document for project ${projectId}, item ${itemId}, file ${fileName}`);

    const r = await authenticatedFetch(`${API_URL}/action_items/${projectId}/items/${itemId}/document/${encodeURIComponent(fileName)}`, {
      method: 'GET'
    });

    if (!r.ok) {
      console.error('Failed to get action item document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    // Check if response is JSON (metadata) or file content
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data: DocumentInterface = await r.json();
      console.log('Action item document loaded:', data);
      return data;
    } else {
      // Return the response for file download
      return r as any;
    }
  } catch (e: Error | any) {
    console.error('Error in getActionItemDocument:', e);
    throw new Error(e.message || 'Failed to get action item document');
  }
};

/**
 * Create an action item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Action item ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Created document info
 */
export const createActionItemDocument = async (
  _token: string,
  projectId: string,
  itemId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Creating action item document for project ${projectId}, item ${itemId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await authenticatedFetch(`${API_URL}/action_items/${projectId}/items/${itemId}/document`, {
      method: 'POST',
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to create action item document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Action item document created:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in createActionItemDocument:', e);
    throw new Error(e.message || 'Failed to create action item document');
  }
};

/**
 * Update an action item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Action item ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Updated document info
 */
export const updateActionItemDocument = async (
  _token: string,
  projectId: string,
  itemId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Updating action item document for project ${projectId}, item ${itemId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await authenticatedFetch(`${API_URL}/action_items/${projectId}/items/${itemId}/document`, {
      method: 'PUT',
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to update action item document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Action item document updated:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in updateActionItemDocument:', e);
    throw new Error(e.message || 'Failed to update action item document');
  }
};

/**
 * Delete an action item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Action item ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteActionItemDocument = async (
  _token: string,
  projectId: string,
  itemId: string
): Promise<boolean> => {
  try {
    console.log(`Deleting action item document for project ${projectId}, item ${itemId}`);

    const r = await authenticatedFetch(`${API_URL}/action_items/${projectId}/items/${itemId}/document`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      console.error('Failed to delete action item document:', r.status, r.statusText);

      if (r.status === 401) {
        const authError = new Error('Authentication failed. Your session may have expired. Please refresh the page and try again.');
        (authError as any).isAuthError = true;
        (authError as any).statusCode = 401;
        throw authError;
      }

      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    console.log('Action item document deleted successfully');
    return true;
  } catch (e: Error | any) {
    console.error('Error in deleteActionItemDocument:', e);
    if (e.isAuthError) {
      throw e;
    }
    throw new Error(e.message || 'Failed to delete action item document');
  }
}; 